import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, CalendarDays, Loader2, Plus, SlidersHorizontal } from 'lucide-react';
import { EditActionBar } from '../shared/EditActionBar';
import { ExportXlsxButton } from '../shared/ExportXlsxButton';
import type { Driver13Data, PeakQuarterRow, DecemberPeakRow, Driver13Row } from '../../../api/apiTypesConductRisk';
import { useConductRiskDriver } from '../../../api/hooks/useConductRiskDriver';
import { fetchExportConductRiskDriverXlsx } from '../../../api/functionApi';

const numCls = 'w-24 text-right bg-white text-[#1B365D] border border-amber-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-amber-500 tabular-nums';
const txtCls = 'w-full min-w-[7rem] bg-white border border-amber-300 rounded px-1.5 py-0.5 text-xs text-[#1B365D] focus:outline-none focus:border-amber-500';

/* ─── Helpers ────────────────────────────────────────────── */
const fmtE = (n: number) =>
  new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(n) + ' €';
const fmtPct2 = (n: number) => `${n.toFixed(2)}%`;
// Varianti null-safe per il tab "Tutte le analizzate": source_rows puo' avere
// revenue/budget a null quando la fonte Evoluzione Ricavi non ha le colonne mensili.
const fmtEn   = (n: number | null) => (n == null ? '—' : fmtE(n));
const fmtPct2n = (n: number | null) => (n == null ? '—' : fmtPct2(n));

type TabKey = 'q1' | 'q2' | 'q3' | 'dec' | 'all';

/* ─── Factory righe vuote (append inline in edit) ─────────── */
const emptyDriver13Row = (): Driver13Row => ({
  branch_name:            '',
  clone_uo:               '',
  area:                   '',
  regional_division:      '',
  q1:                     0,
  q2:                     0,
  q3:                     0,
  q4:                     0,
  filiale_chiusa:         false,
  revenue_jan_dec:        0,
  revenue_jan_nov:        0,
  budget_achievement_pct: 0,
  total:                  0,
  arithmetic_mean:        0,
});

const emptyPeakQuarterRow = (): PeakQuarterRow => ({
  branch_name:       '',
  clone_uo:          '',
  area:              '',
  regional_division: '',
  q1:                0,
  q2:                0,
  q3:                0,
  q4:                0,
  focus_quarter:     0,
  total:             0,
  arithmetic_mean:   0,
  delta_vs_mean:     0,
});

const emptyDecemberPeakRow = (): DecemberPeakRow => ({
  branch_name:                    '',
  clone_uo:                       '',
  area:                           '',
  regional_division:              '',
  revenue_jan_dec:                0,
  revenue_jan_nov:                0,
  budget_achievement_pct:         0,
  budget_achievement_pct_jan_nov: 0,
  expected_monthly_budget_pct:    0,
  december_deviation_pct:         0,
});

/* ─── Main ───────────────────────────────────────────────── */
const EstensioneAnalisiPicchiRicaviContainer = ({ reportId }: { reportId: string }) => {
  const { data: remoteData, loading, error, save, isReadOnly } = useConductRiskDriver<Driver13Data>(reportId, 13);

  const [data,        setData]        = useState<Driver13Data | null>(null);
  const [savedData,   setSavedData]   = useState<Driver13Data | null>(null);
  const [sogliaQ,     setSogliaQ]     = useState<number>(50);   // % trimestrale
  const [sogliaDec,   setSogliaDec]   = useState<number>(8.33); // % dicembre
  const [autoSaving,  setAutoSaving]  = useState(false);
  const [isEditing,   setIsEditing]   = useState(false);
  const [tab,         setTab]         = useState<TabKey>('q1');
  const debounceRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initializedRef = useRef(false);

  const handleAnnulla = () => { if (savedData) setData(savedData); setIsEditing(false); };
  const handleSalva   = async () => {
    if (!data) return;
    await save(data);
    setSavedData(data);
    setIsEditing(false);
  };

  /* ─── Aggiornamento libero delle righe (match per riferimento, nessun
   *  ricalcolo lato FE). Ogni tab edita direttamente la lista che mostra:
   *  "Tutte le analizzate" -> source_rows, Picchi Q* -> q*_rows,
   *  Picchi Dicembre -> december_rows. ─── */

  /** Lista q* attualmente visualizzata (dipende dal tab). */
  const qListKey = (): 'q1_rows' | 'q2_rows' | 'q3_rows' =>
    tab === 'q1' ? 'q1_rows' : tab === 'q2' ? 'q2_rows' : 'q3_rows';

  const updateQNum = (row: PeakQuarterRow, field: keyof PeakQuarterRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    const key = qListKey();
    setData(prev => (prev ? { ...prev, [key]: prev[key].map(r => r === row ? { ...r, [field]: val } : r) } : prev));
  };
  const updateQText = (row: PeakQuarterRow, field: 'clone_uo' | 'branch_name' | 'area' | 'regional_division', value: string) => {
    const key = qListKey();
    setData(prev => (prev ? { ...prev, [key]: prev[key].map(r => r === row ? { ...r, [field]: value } : r) } : prev));
  };

  const updateAllNum = (row: Driver13Row, field: keyof Driver13Row, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? { ...prev, source_rows: prev.source_rows.map(r => r === row ? { ...r, [field]: val } : r) } : prev));
  };
  const updateAllText = (row: Driver13Row, field: 'clone_uo' | 'branch_name' | 'area' | 'regional_division', value: string) => {
    setData(prev => (prev ? { ...prev, source_rows: prev.source_rows.map(r => r === row ? { ...r, [field]: value } : r) } : prev));
  };

  const updateDecNum = (row: DecemberPeakRow, field: keyof DecemberPeakRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev && prev.december_rows ? { ...prev, december_rows: prev.december_rows.map(r => r === row ? { ...r, [field]: val } : r) } : prev));
  };
  const updateDecText = (row: DecemberPeakRow, field: 'clone_uo' | 'branch_name' | 'area' | 'regional_division', value: string) => {
    setData(prev => (prev && prev.december_rows ? { ...prev, december_rows: prev.december_rows.map(r => r === row ? { ...r, [field]: value } : r) } : prev));
  };

  const addAllRow = () => {
    setData(prev => (prev ? { ...prev, source_rows: [...prev.source_rows, emptyDriver13Row()] } : prev));
  };
  const addDecRow = () => {
    setData(prev => (prev && prev.december_rows ? { ...prev, december_rows: [...prev.december_rows, emptyDecemberPeakRow()] } : prev));
  };
  const addQRow = () => {
    const key = qListKey();
    setData(prev => (prev ? { ...prev, [key]: [...prev[key], emptyPeakQuarterRow()] } : prev));
  };

  useEffect(() => {
    if (!remoteData) return;
    // Il BE puo' restituire le liste trimestrali come null; normalizzo a
    // array vuoti per evitare crash su .length / spread. `december_rows` invece
    // mantiene la distinzione null (analisi non eseguibile) vs [] (nessun flag).
    const normalized: Driver13Data = {
      ...remoteData,
      source_rows:   remoteData.source_rows   ?? [],
      q1_rows:       remoteData.q1_rows       ?? [],
      q2_rows:       remoteData.q2_rows       ?? [],
      q3_rows:       remoteData.q3_rows       ?? [],
      december_rows: remoteData.december_rows ?? null,
    };
    setData(normalized);
    setSavedData(normalized);
    if (!initializedRef.current) {
      setSogliaQ(remoteData.thresholds.quarter_deviation_pct);
      setSogliaDec(remoteData.thresholds.december_deviation_pct);
      initializedRef.current = true;
    }
  }, [remoteData]);

  // Autosave soglie (~600ms debounced). Attivo solo fuori editing.
  useEffect(() => {
    if (!data || !savedData || isReadOnly || isEditing) return;
    if (
      sogliaQ   === savedData.thresholds.quarter_deviation_pct &&
      sogliaDec === savedData.thresholds.december_deviation_pct
    ) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const payload: Driver13Data = {
        ...data,
        thresholds: {
          quarter_deviation_pct:  sogliaQ,
          december_deviation_pct: sogliaDec,
        },
      };
      setAutoSaving(true);
      try {
        await save(payload);
        setSavedData(payload);
      } catch (err) {
        console.error('Autosave soglie Driver 13 fallito:', err);
        setSogliaQ(savedData.thresholds.quarter_deviation_pct);
        setSogliaDec(savedData.thresholds.december_deviation_pct);
      } finally {
        setAutoSaving(false);
      }
    }, 600);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [sogliaQ, sogliaDec, savedData, isReadOnly, isEditing, data, save]);

  const qRows: PeakQuarterRow[] = useMemo(() => {
    if (!data || tab === 'dec') return [];
    const src = tab === 'q1' ? data.q1_rows : tab === 'q2' ? data.q2_rows : data.q3_rows;
    return [...src].sort((a, b) => Math.abs(b.delta_vs_mean) - Math.abs(a.delta_vs_mean));
  }, [data, tab]);

  const decRows: DecemberPeakRow[] = useMemo(() => {
    if (!data || tab !== 'dec' || !data.december_rows) return [];
    return [...data.december_rows].sort((a, b) => b.december_deviation_pct - a.december_deviation_pct);
  }, [data, tab]);

  // Tab "Tutte le analizzate": la popolazione completa (source_rows), non solo
  // le filiali flaggate. Ordinata per totale ricavi decrescente.
  const allRows: Driver13Row[] = useMemo(() => {
    if (!data || tab !== 'all') return [];
    return [...data.source_rows].sort((a, b) => b.total - a.total);
  }, [data, tab]);

  // `december_rows === null` = la sotto-analisi dicembre non e' eseguibile
  // (la fonte Evoluzione Ricavi non conteneva le colonne mensili).
  const decNotAvailable = data?.december_rows == null;

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per questo driver.</div>;
  }

  const TABS: { key: TabKey; label: string; count: number; complete?: boolean }[] = [
    { key: 'q1',  label: 'Picchi Q1',        count: data.q1_rows.length },
    { key: 'q2',  label: 'Picchi Q2',        count: data.q2_rows.length },
    { key: 'q3',  label: 'Picchi Q3',        count: data.q3_rows.length },
    { key: 'dec', label: 'Picchi Dicembre',  count: data.december_rows?.length ?? 0 },
    { key: 'all', label: 'Tutte le analizzate', count: data.source_rows.length, complete: true },
  ];

  return (
    <div className="p-6 space-y-5">

      {/* Banner Investigation + Edit bar */}
      <div className="flex items-start gap-3">
        <div className="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50/60 border border-amber-100">
          <Activity className="w-4 h-4 text-amber-700 shrink-0" />
          <p className="text-xs text-amber-900 leading-relaxed">
            <span className="font-semibold">Investigation</span> · Identificazione delle filiali che mostrano dei picchi di ricavi nel Q1, Q2 e Q3. Inoltre, analisi dei ricavi registrati nel mese di dicembre dalle filiali che non avevano raggiunto il budget annuale dei ricavi al termine di novembre e che lo hanno successivamente raggiunto entro fine anno. La ratio sottostante tale investigation risiede nell'individuare eventuali picchi di ricavi e incrementi mensili dei ricavi significativamente superiori rispetto a quelli attesi.
          </p>
        </div>
        <ExportXlsxButton
          fetchBlob={() => fetchExportConductRiskDriverXlsx(reportId, 'Driver13')}
          fileName={`Estensione_Analisi_Picchi_Ricavi_${data.period}`}
        />
        <EditActionBar
          isEditing={isEditing}
          onProcedi={() => setIsEditing(true)}
          onAnnulla={handleAnnulla}
          onSalva={handleSalva}
          disabled={isReadOnly}
        />
      </div>

      {/* Soglie */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
            <SlidersHorizontal className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-amber-800">Soglia scarto trimestrale</p>
            <p className="text-[10px] text-gray-400 mt-0.5">Q1 / Q2 / Q3 vs media · default {data.thresholds.quarter_deviation_pct}%</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {autoSaving && <Loader2 className="w-3.5 h-3.5 text-amber-700/60 animate-spin" />}
            <input
              type="number"
              min={1}
              max={500}
              value={sogliaQ}
              disabled={isReadOnly || isEditing}
              onChange={e => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setSogliaQ(Math.max(1, Math.min(500, v)));
              }}
              className="w-16 text-right text-sm font-bold text-amber-800 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-xs font-medium text-gray-400">%</span>
          </div>
        </div>

        <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
            <CalendarDays className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-amber-800">Soglia scostamento dicembre</p>
            <p className="text-[10px] text-gray-400 mt-0.5">Budget raggiunto solo a dicembre · default {data.thresholds.december_deviation_pct}%</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <input
              type="number"
              min={0}
              max={100}
              step={0.01}
              value={sogliaDec}
              disabled={isReadOnly || isEditing}
              onChange={e => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setSogliaDec(Math.max(0, Math.min(100, v)));
              }}
              className="w-20 text-right text-sm font-bold text-amber-800 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-xs font-medium text-gray-400">%</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
              tab === t.key
                ? 'bg-white text-amber-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
            <span className={`ml-2 inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
              t.complete ? 'bg-gray-200 text-gray-700' : 'bg-amber-100 text-amber-800'
            }`}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.18 }}
        >
          {tab === 'all' ? (
            allRows.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-sm text-gray-400">
                Nessuna filiale analizzata disponibile.
              </div>
            ) : (
              <>
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <p className="text-xs font-semibold text-[#1B365D]">Tutte le filiali analizzate · {data.period}</p>
                  <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full border text-[#1B365D] bg-[#1B365D]/5 border-[#1B365D]/10">
                    {allRows.length} filiali analizzate
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                        <th className="text-left  px-4 py-2.5 font-semibold">CloneUo</th>
                        <th className="text-left  px-4 py-2.5 font-semibold">Filiale</th>
                        <th className="text-left  px-4 py-2.5 font-semibold">Area</th>
                        <th className="text-left  px-4 py-2.5 font-semibold">Dir. Regionale</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Q1</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Q2</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Q3</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Q4</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Media trim.</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Ricavi Gen-Nov</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Ricavi Gen-Dic</th>
                        <th className="text-right px-4 py-2.5 font-semibold">% Bdg Annuo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {allRows.map((row, i) => (
                        <motion.tr
                          key={i}
                          initial={{ opacity: 0, x: -4 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: Math.min(i * 0.02, 0.4) }}
                          className="hover:bg-amber-50/30 transition-colors"
                        >
                          <td className="px-4 py-3 font-mono text-[10px] text-gray-500 whitespace-nowrap">
                            {isEditing ? <input type="text" className={txtCls} value={row.clone_uo} onChange={e => updateAllText(row, 'clone_uo', e.target.value)} /> : row.clone_uo}
                          </td>
                          <td className="px-4 py-3 font-medium text-[#1B365D] whitespace-nowrap">
                            {isEditing ? <input type="text" className={txtCls} value={row.branch_name} onChange={e => updateAllText(row, 'branch_name', e.target.value)} /> : row.branch_name}
                          </td>
                          <td className="px-4 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                            {isEditing ? <input type="text" className={txtCls} value={row.area} onChange={e => updateAllText(row, 'area', e.target.value)} /> : row.area}
                          </td>
                          <td className="px-4 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                            {isEditing ? <input type="text" className={txtCls} value={row.regional_division} onChange={e => updateAllText(row, 'regional_division', e.target.value)} /> : row.regional_division}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 tabular-nums whitespace-nowrap">
                            {isEditing
                              ? <input type="number" className={numCls} value={row.q1} onChange={e => updateAllNum(row, 'q1', e.target.value)} />
                              : fmtE(row.q1)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 tabular-nums whitespace-nowrap">
                            {isEditing
                              ? <input type="number" className={numCls} value={row.q2} onChange={e => updateAllNum(row, 'q2', e.target.value)} />
                              : fmtE(row.q2)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 tabular-nums whitespace-nowrap">
                            {isEditing
                              ? <input type="number" className={numCls} value={row.q3} onChange={e => updateAllNum(row, 'q3', e.target.value)} />
                              : fmtE(row.q3)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 tabular-nums whitespace-nowrap">
                            {isEditing
                              ? <input type="number" className={numCls} value={row.q4} onChange={e => updateAllNum(row, 'q4', e.target.value)} />
                              : fmtE(row.q4)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">
                            {isEditing
                              ? <input type="number" className={numCls} value={row.arithmetic_mean} onChange={e => updateAllNum(row, 'arithmetic_mean', e.target.value)} />
                              : fmtE(row.arithmetic_mean)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 tabular-nums whitespace-nowrap">
                            {isEditing
                              ? <input type="number" className={numCls} value={row.revenue_jan_nov ?? ''} onChange={e => updateAllNum(row, 'revenue_jan_nov', e.target.value)} />
                              : fmtEn(row.revenue_jan_nov)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600 tabular-nums whitespace-nowrap">
                            {isEditing
                              ? <input type="number" className={numCls} value={row.revenue_jan_dec ?? ''} onChange={e => updateAllNum(row, 'revenue_jan_dec', e.target.value)} />
                              : fmtEn(row.revenue_jan_dec)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">
                            {isEditing
                              ? <input type="number" step="0.01" className={numCls} value={row.budget_achievement_pct ?? ''} onChange={e => updateAllNum(row, 'budget_achievement_pct', e.target.value)} />
                              : fmtPct2n(row.budget_achievement_pct)}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {isEditing && (
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => addAllRow()}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                  </button>
                </div>
              )}
              </>
            )
          ) : tab === 'dec' ? (
            decNotAvailable ? (
              <div className="bg-white rounded-xl border border-amber-100 p-8 text-center text-sm text-amber-700 bg-amber-50/40">
                Analisi di dicembre non disponibile: la fonte Evoluzione Ricavi non contiene le colonne mensili necessarie.
              </div>
            ) : decRows.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-sm text-gray-400">
                Nessuna filiale flaggata per dicembre.
              </div>
            ) : (
              <>
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                        <th className="text-left  px-4 py-2.5 font-semibold">CloneUo</th>
                        <th className="text-left  px-4 py-2.5 font-semibold">Filiale</th>
                        <th className="text-left  px-4 py-2.5 font-semibold">Area</th>
                        <th className="text-left  px-4 py-2.5 font-semibold">Dir. Regionale</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Ricavi Gen-Dic</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Ricavi Gen-Nov</th>
                        <th className="text-right px-4 py-2.5 font-semibold">% Bdg Annuo</th>
                        <th className="text-right px-4 py-2.5 font-semibold">% Bdg Gen-Nov</th>
                        <th className="text-right px-4 py-2.5 font-semibold text-amber-600">Scost. Dic.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {decRows.map((row, i) => (
                        <motion.tr
                          key={i}
                          initial={{ opacity: 0, x: -4 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.02 }}
                          className="hover:bg-amber-50/30 transition-colors"
                        >
                          <td className="px-4 py-3 font-mono text-[10px] text-gray-500 whitespace-nowrap">
                            {isEditing ? <input type="text" className={txtCls} value={row.clone_uo} onChange={e => updateDecText(row, 'clone_uo', e.target.value)} /> : row.clone_uo}
                          </td>
                          <td className="px-4 py-3 font-medium text-[#1B365D] whitespace-nowrap">
                            {isEditing ? <input type="text" className={txtCls} value={row.branch_name} onChange={e => updateDecText(row, 'branch_name', e.target.value)} /> : row.branch_name}
                          </td>
                          <td className="px-4 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                            {isEditing ? <input type="text" className={txtCls} value={row.area} onChange={e => updateDecText(row, 'area', e.target.value)} /> : row.area}
                          </td>
                          <td className="px-4 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                            {isEditing ? <input type="text" className={txtCls} value={row.regional_division} onChange={e => updateDecText(row, 'regional_division', e.target.value)} /> : row.regional_division}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600 tabular-nums whitespace-nowrap">
                            {isEditing
                              ? <input type="number" className={numCls} value={row.revenue_jan_dec} onChange={e => updateDecNum(row, 'revenue_jan_dec', e.target.value)} />
                              : fmtE(row.revenue_jan_dec)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 tabular-nums whitespace-nowrap">
                            {isEditing
                              ? <input type="number" className={numCls} value={row.revenue_jan_nov} onChange={e => updateDecNum(row, 'revenue_jan_nov', e.target.value)} />
                              : fmtE(row.revenue_jan_nov)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">
                            {isEditing
                              ? <input type="number" step="0.01" className={numCls} value={row.budget_achievement_pct} onChange={e => updateDecNum(row, 'budget_achievement_pct', e.target.value)} />
                              : fmtPct2(row.budget_achievement_pct)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 whitespace-nowrap">
                            {isEditing
                              ? <input type="number" step="0.01" className={numCls} value={row.budget_achievement_pct_jan_nov} onChange={e => updateDecNum(row, 'budget_achievement_pct_jan_nov', e.target.value)} />
                              : fmtPct2(row.budget_achievement_pct_jan_nov)}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {isEditing
                              ? <input type="number" step="0.01" className={numCls} value={row.december_deviation_pct} onChange={e => updateDecNum(row, 'december_deviation_pct', e.target.value)} />
                              : <span className="inline-flex px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-bold text-[10px]">
                                  {fmtPct2(row.december_deviation_pct)}
                                </span>}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {isEditing && (
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => addDecRow()}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                  </button>
                </div>
              )}
              </>
            )
          ) : (
            qRows.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-sm text-gray-400">
                Nessun picco identificato per questo trimestre.
              </div>
            ) : (
              <>
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                        <th className="text-left  px-4 py-2.5 font-semibold">CloneUo</th>
                        <th className="text-left  px-4 py-2.5 font-semibold">Filiale</th>
                        <th className="text-left  px-4 py-2.5 font-semibold">Area</th>
                        <th className="text-left  px-4 py-2.5 font-semibold">Dir. Regionale</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Q1</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Q2</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Q3</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Q4</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Media trim.</th>
                        <th className="text-right px-4 py-2.5 font-semibold text-amber-600">Delta {tab.toUpperCase()} − Media</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {qRows.map((row, i) => (
                        <motion.tr
                          key={i}
                          initial={{ opacity: 0, x: -4 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.02 }}
                          className="hover:bg-amber-50/30 transition-colors"
                        >
                          <td className="px-4 py-3 font-mono text-[10px] text-gray-500 whitespace-nowrap">
                            {isEditing ? <input type="text" className={txtCls} value={row.clone_uo} onChange={e => updateQText(row, 'clone_uo', e.target.value)} /> : row.clone_uo}
                          </td>
                          <td className="px-4 py-3 font-medium text-[#1B365D] whitespace-nowrap">
                            {isEditing ? <input type="text" className={txtCls} value={row.branch_name} onChange={e => updateQText(row, 'branch_name', e.target.value)} /> : row.branch_name}
                          </td>
                          <td className="px-4 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                            {isEditing ? <input type="text" className={txtCls} value={row.area} onChange={e => updateQText(row, 'area', e.target.value)} /> : row.area}
                          </td>
                          <td className="px-4 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                            {isEditing ? <input type="text" className={txtCls} value={row.regional_division} onChange={e => updateQText(row, 'regional_division', e.target.value)} /> : row.regional_division}
                          </td>
                          <td className={`px-4 py-3 text-right whitespace-nowrap ${tab === 'q1' ? 'font-semibold text-amber-700 bg-amber-50/40' : 'text-gray-500'}`}>
                            {isEditing
                              ? <input type="number" className={numCls} value={row.q1} onChange={e => updateQNum(row, 'q1', e.target.value)} />
                              : fmtE(row.q1)}
                          </td>
                          <td className={`px-4 py-3 text-right whitespace-nowrap ${tab === 'q2' ? 'font-semibold text-amber-700 bg-amber-50/40' : 'text-gray-500'}`}>
                            {isEditing
                              ? <input type="number" className={numCls} value={row.q2} onChange={e => updateQNum(row, 'q2', e.target.value)} />
                              : fmtE(row.q2)}
                          </td>
                          <td className={`px-4 py-3 text-right whitespace-nowrap ${tab === 'q3' ? 'font-semibold text-amber-700 bg-amber-50/40' : 'text-gray-500'}`}>
                            {isEditing
                              ? <input type="number" className={numCls} value={row.q3} onChange={e => updateQNum(row, 'q3', e.target.value)} />
                              : fmtE(row.q3)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 whitespace-nowrap">
                            {isEditing
                              ? <input type="number" className={numCls} value={row.q4} onChange={e => updateQNum(row, 'q4', e.target.value)} />
                              : fmtE(row.q4)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">
                            {isEditing
                              ? <input type="number" className={numCls} value={row.arithmetic_mean} onChange={e => updateQNum(row, 'arithmetic_mean', e.target.value)} />
                              : fmtE(row.arithmetic_mean)}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {isEditing
                              ? <input type="number" className={numCls} value={row.delta_vs_mean} onChange={e => updateQNum(row, 'delta_vs_mean', e.target.value)} />
                              : <span className="inline-flex px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-bold text-[10px]">
                                  {fmtE(row.delta_vs_mean)}
                                </span>}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {isEditing && (
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => addQRow()}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                  </button>
                </div>
              )}
              </>
            )
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default EstensioneAnalisiPicchiRicaviContainer;
